import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { newProductReportApi, FJ780, CreateRequest, UpdateRequest, pickActiveChildFamilies } from '../../../services/newProductReportApi';

export type Mode = 'browse' | 'add' | 'edit';

const defaultFormValues = {
  fj780_02: '',
  fj780_03: null as string | null,
  fj780_10: '',
  fj780_11: '',
  fj780_12: '',
  fj780_13: '',
  fj780_14: '',
  fj780_15: 0,
  fj780_16: '',
  fj780_17: 0,
  fj780_171: '',
  fj780_18: 'NTD',
  fj780_19: 0,
  fj780_191: 0,
  fj780_192: 0,
  fj780_21: 0,
  fj780_211: 0,
  fj780_22: 0,
  fj780_221: 0,
  fj780_23: 0,
  fj780_24: 0,
};

export function useNewProductReportForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FJ780[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState(defaultFormValues);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';
  const isApproved = current?.fj780_80 === 'P';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await newProductReportApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setFormValues({
        fj780_02: current.fj780_02,
        fj780_03: current.fj780_03,
        fj780_10: current.fj780_10,
        fj780_11: current.fj780_11,
        fj780_12: current.fj780_12,
        fj780_13: current.fj780_13,
        fj780_14: current.fj780_14,
        fj780_15: current.fj780_15,
        fj780_16: current.fj780_16,
        fj780_17: current.fj780_17,
        fj780_171: current.fj780_171,
        fj780_18: current.fj780_18,
        fj780_19: current.fj780_19,
        fj780_191: current.fj780_191,
        fj780_192: current.fj780_192,
        fj780_21: current.fj780_21,
        fj780_211: current.fj780_211,
        fj780_22: current.fj780_22,
        fj780_221: current.fj780_221,
        fj780_23: current.fj780_23,
        fj780_24: current.fj780_24,
      });
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Field updater
  const updateField = useCallback((field: string, value: string | number | null) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
  }, []);

  // Start add
  const startAdd = () => {
    setMode('add');
    setFormValues({ ...defaultFormValues });
  };

  // Start edit
  const startEdit = () => {
    if (!current || isApproved) return;
    setMode('edit');
  };

  // Cancel
  const cancel = () => {
    setMode('browse');
    if (current) {
      setFormValues({
        fj780_02: current.fj780_02,
        fj780_03: current.fj780_03,
        fj780_10: current.fj780_10,
        fj780_11: current.fj780_11,
        fj780_12: current.fj780_12,
        fj780_13: current.fj780_13,
        fj780_14: current.fj780_14,
        fj780_15: current.fj780_15,
        fj780_16: current.fj780_16,
        fj780_17: current.fj780_17,
        fj780_171: current.fj780_171,
        fj780_18: current.fj780_18,
        fj780_19: current.fj780_19,
        fj780_191: current.fj780_191,
        fj780_192: current.fj780_192,
        fj780_21: current.fj780_21,
        fj780_211: current.fj780_211,
        fj780_22: current.fj780_22,
        fj780_221: current.fj780_221,
        fj780_23: current.fj780_23,
        fj780_24: current.fj780_24,
      });
    }
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        const payload: CreateRequest = { ...formValues };
        const created = await newProductReportApi.create(payload);
        message.success('新增成功');
        const data = await loadRecords();
        const idx = data.findIndex(r => r.fj780_01 === created.fj780_01);
        setCurrentIdx(idx >= 0 ? idx : 0);
      } else {
        // The backend replaces every active child family in the same transaction.
        // Preserve all 23 arrays when this header-only screen edits an existing row.
        const payload: UpdateRequest = {
          ...formValues,
          ...pickActiveChildFamilies(current!),
        };
        await newProductReportApi.update(current!.fj780_01, payload);
        message.success('修改成功');
        const data = await loadRecords();
        const idx = data.findIndex(r => r.fj780_01 === current!.fj780_01);
        setCurrentIdx(idx >= 0 ? idx : 0);
      }
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await newProductReportApi.delete(current.fj780_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error('刪除失敗');
      }
    }
  };

  // Approve
  const doApprove = async () => {
    if (!current) return;
    try {
      await newProductReportApi.approve(current.fj780_01, { fj780_80: 'P' });
      message.success('核准成功');
      await loadRecords();
    } catch (err: any) {
      message.error(err?.response?.data?.error || '核准失敗');
    }
  };

  // Quick search
  const quickSearch = async (keyword: string) => {
    if (isEditing) return;
    setLoading(true);
    try {
      const res = await newProductReportApi.list(1, 1000, keyword);
      setRecords(res.data || []);
      if ((res.data || []).length > 0) {
        setCurrentIdx(0);
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('查詢失敗');
    } finally {
      setLoading(false);
    }
  };

  return {
    mode, isEditing, isApproved, loading,
    records, current, currentIdx,
    formValues, updateField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, doApprove, quickSearch, loadRecords,
  };
}
